import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { getApplication } from "../../API/applicationAPI";
import { Application } from "../../types/application";
import UscisFeeDecision from "./UscisFeeDecision";
import UscisFeeQuestionnaire from "./UscisFeeQuestionnaire";
import UscisFeeSummary from "./UscisFeeSummary";
import { createCheckoutSession } from "../../API/paymentAPI";
import { payUscisFeeNow } from "../../API/applicationAPI";
import { canAccessUscisFeeFlow } from "../../utils/uscisFeeAccess";
import { FaCheckCircle, FaCreditCard, FaFileInvoiceDollar, FaLock } from "react-icons/fa";

const UscisFee: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingCheckout, setCreatingCheckout] = useState(false);

  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) {
        setError("Application ID is required");
        setLoading(false);
        return;
      }

      // Reset loading state when re-fetching
      setLoading(true);
      setError(null);

      try {
        console.log("Fetching application:", id);
        const app = await getApplication(parseInt(id));
        console.log("Fetched application:", app);
        console.log("Application stage:", app.stage);
        setApplication(app);
      } catch (err: any) {
        console.error("Error fetching application:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load application. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
    // Re-fetch when id, location pathname, or search params change
    // This ensures we get the latest application state after questionnaire submission
  }, [id, location.pathname, location.search]);

  // "Pay later" flow: charge only the USCIS fee (package already paid) and
  // redirect to Stripe. Used when the applicant opted out earlier.
  const handlePayFeeNow = async () => {
    if (!application?.id) return;
    setCreatingCheckout(true);
    setError(null);
    try {
      const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
      const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;
      const { checkoutUrl } = await payUscisFeeNow(application.id, successUrl, cancelUrl);
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to start USCIS fee payment. Please try again."
      );
      setCreatingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1B365D] mx-auto mb-4"></div>
              <p className="text-[#1B365D] text-lg">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-[#1B365D] mb-2">
              Error Loading Application
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-6 py-3 rounded-[50px] hover:bg-[#F29449]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <div className="text-center">
            <p className="text-gray-600">Application not found.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!canAccessUscisFeeFlow(application)) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <div className="text-center max-w-lg mx-auto">
            <h1 className="text-2xl font-bold text-[#1B365D] mb-3">
              USCIS fee unavailable
            </h1>
            <p className="text-gray-600 mb-6">
              This step is only available after you pass eligibility and choose a
              package. If you were not found eligible, you cannot pay USCIS fees
              through this application.
            </p>
            <Link
              to="/dashboard"
              className="inline-block bg-[#1B365D] text-white font-semibold px-8 py-3 rounded-[50px] hover:opacity-90"
            >
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // "Pay later" flow takes precedence over the stage-based routing: a premium
  // applicant who opted out earlier sees the fee summary and a pay button that
  // charges only the USCIS fee (no package re-charge), at any stage.
  if (application.uscisFee?.canPayNow) {
    return (
      <div className="page_main page_main-after-login mt-[100px] bg-[#F7F8FA] pb-[100px] pt-[40px]">
        <div className="mx-auto w-full max-w-5xl px-4">
          <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
            <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="bg-[#1B365D] px-6 py-8 text-white md:px-10 md:py-12">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-semibold">
                  <FaFileInvoiceDollar aria-hidden="true" />
                  USCIS filing fee
                </div>
                <h1 className="mb-4 max-w-xl text-[30px] font-bold leading-tight md:text-[42px]">
                  Complete your USCIS fee payment
                </h1>
                <p className="max-w-xl text-base leading-7 text-white/85 md:text-lg">
                  You chose to pay this later. Paying now charges only the USCIS filing
                  fee; your LetsGetVisa package is already paid.
                </p>

                <div className="mt-8 grid gap-3 text-sm text-white/90 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                    <FaCheckCircle className="mb-3 text-[#80EFD9]" aria-hidden="true" />
                    Package paid
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                    <FaLock className="mb-3 text-[#F4A261]" aria-hidden="true" />
                    Secure checkout
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                    <FaCreditCard className="mb-3 text-[#F4A261]" aria-hidden="true" />
                    Fee only
                  </div>
                </div>
              </div>

              <div className="px-5 py-6 md:px-8 md:py-10">
                <UscisFeeSummary uscisFee={application.uscisFee} showStatus={false} />
                {error && (
                  <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-[14px] text-red-700">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handlePayFeeNow}
                  disabled={creatingCheckout}
                  className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#F4A261] px-8 py-3 text-base font-bold text-[#1B365D] transition hover:bg-[#F29449] disabled:opacity-60"
                >
                  <FaCreditCard aria-hidden="true" />
                  {creatingCheckout ? "Starting payment..." : "Pay USCIS fee"}
                </button>
                <p className="mt-4 text-center text-sm text-[#6B7280]">
                  You will be redirected to Stripe to complete this government filing fee payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Route to appropriate component based on stage
  // Flow: Premium package -> questionnaire (package_selected stage) -> decision stage (after questionnaire submission)
  // Note: Basic plan users never reach this page - they go directly to checkout
  switch (application.stage) {
    case "uscis_fee_questionnaire":
      // Questionnaire not yet submitted - show questionnaire component
      return <UscisFeeQuestionnaire />;
    case "package_selected":
      // If stage is package_selected and user is on USCIS Fee page, questionnaire will always be asked
      // (Basic plan users are never redirected here - they go directly to checkout)
      return <UscisFeeQuestionnaire />;

    case "uscis_fee_decision":
      // Decision stage (for non-premium packages or other flows)
      return <UscisFeeDecision />;

    default:
      // For any other stage, show decision component if fee decision hasn't been made
      // or show summary if fee is already computed
      if (
        application.uscisFee &&
        application.uscisFee.status !== "not_applicable" &&
        application.uscisFee.status !== "skipped"
      ) {
        return (
          <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
            <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
              <h1 className="font-bold text-center mb-4 lg:text-[40px] md:text-[30px] text-[26px]">
                USCIS Fee Information
              </h1>
              <UscisFeeSummary uscisFee={application.uscisFee} />
            </div>
          </div>
        );
      }

      // Default to decision component (for non-premium packages or legacy flows)
      return <UscisFeeDecision />;
  }
};

export default UscisFee;
